import {
  FC,
  ReactNode,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { LeftOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { Button, Layout, Space, Tooltip } from 'antd';
import styled from 'styled-components';
import { useMediaQuery, useOnClickOutside } from 'usehooks-ts';
import { useRouterContext } from '@/shared/router';
import { useThemeContext, themeConfig } from '@/shared/theme';
import { Row } from '@/shared/ui';
import { PageLayoutNotifications } from './PageLayoutNotifications';
import { PageLayoutSettingsMenuBtn } from './PageLayoutSettingsMenuBtn';

type Props = {
  headerContent?: ReactNode;
  headerStyle?: React.CSSProperties;
  sidebarLeftContent?: ReactNode;
  sidebarRightContent?: ReactNode;
  footerContent?: ReactNode;
  children?: ReactNode;
  sidebarLeftCollapsed?: boolean;
  sidebarRightCollapsed?: boolean;
  sidebarLeftWidth?: number | string;
  sidebarRightWidth?: number | string;
  contentStyle?: React.CSSProperties;
  showCloseSidebarButtons?: boolean;
  leftSidebarOpenedTooltip?: string;
  leftSidebarClosedTooltip?: string;
  rightSidebarOpenedTooltip?: string;
  rightSidebarClosedTooltip?: string;
  onLeftSidebarIsOpenChange?: (val: boolean) => void;
  onRightSidebarIsOpenChange?: (val: boolean) => void;
};

export type PageRefProps = {
  openLeftSidebar: () => void;
  closeLeftSidebar: () => void;
  openRightSidebar: () => void;
  closeRightSidebar: () => void;
};

const { Footer, Sider: AntdSider, Content } = Layout;

export const PageLayout: FC<Props> = forwardRef(
  (
    {
      headerContent,
      headerStyle,
      sidebarLeftContent,
      sidebarLeftCollapsed = sidebarLeftContent && false,
      sidebarLeftWidth = 200,
      sidebarRightWidth = 200,
      contentStyle,
      sidebarRightContent,
      sidebarRightCollapsed = sidebarRightContent && false,
      footerContent,
      children,
      showCloseSidebarButtons = false,
      leftSidebarOpenedTooltip = 'Close sidebar',
      leftSidebarClosedTooltip = 'Open sidebar',
      rightSidebarOpenedTooltip = 'Close sidebar',
      rightSidebarClosedTooltip = 'Open sidebar',
      onLeftSidebarIsOpenChange,
      onRightSidebarIsOpenChange,
    },
    ref,
  ) => {
    const { isAuthenticated } = useAuth0();
    const { theme } = useThemeContext();
    const isMobileView = useMediaQuery('(max-width: 768px)');
    const { activeOrganization } = useRouterContext();

    const sidebarLeftRef = useRef(null);
    const sidebarRightRef = useRef(null);

    const [sidebarLeftVisible, setSidebarLeftVisible] =
      useState(!sidebarLeftCollapsed);
    const [sidebarRightVisible, setSidebarRightVisible] = useState(
      !sidebarRightCollapsed,
    );

    useImperativeHandle(
      ref,
      () => ({
        openLeftSidebar: () => setSidebarLeftVisible(true),
        closeLeftSidebar: () => setSidebarLeftVisible(false),
        openRightSidebar: () => setSidebarRightVisible(true),
        closeRightSidebar: () => setSidebarRightVisible(false),
      }),
      [],
    );

    // hide left sidebar on click outside when mobile view
    useOnClickOutside(
      sidebarLeftRef,
      (e) => {
        // ignore if it's a click on the menu/sider action button
        if (
          isMobileView &&
          e.target?.dataset['type'] !== 'menu' &&
          e.target?.dataset['type'] !== 'sidebar-button-left' &&
          e.target?.id !== 'sidebar-button-left-icon'
        ) {
          setSidebarLeftVisible(false);
        }
      },
      'mouseup',
    );
    useOnClickOutside(
      sidebarRightRef,
      (e) => {
        // ignore if it's a click on the menu/sider action button
        if (
          isMobileView &&
          e.target?.dataset['type'] !== 'menu' &&
          e.target?.dataset['type'] !== 'sidebar-button-right' &&
          e.target?.id !== 'sidebar-button-right-icon'
        ) {
          setSidebarRightVisible(false);
        }
      },
      'mouseup',
    );

    useEffect(() => {
      setSidebarLeftVisible(!isMobileView);
      setSidebarRightVisible(!isMobileView);
    }, [isMobileView]);

    useEffect(() => {
      onLeftSidebarIsOpenChange &&
        onLeftSidebarIsOpenChange(sidebarLeftVisible);
    }, [sidebarLeftVisible]);

    useEffect(() => {
      onRightSidebarIsOpenChange &&
        onRightSidebarIsOpenChange(sidebarRightVisible);
    }, [sidebarRightVisible]);

    return (
      <Space
        direction="vertical"
        style={{ width: '100%', height: '100%' }}
        size={[0, 48]}
      >
        <Layout style={containerStyle}>
          {activeOrganization?.readonly && (
            <ReadOnlyBanner justifyContent="center">
              Your organization is in read-only mode.
              <Tooltip title="You can't make any changes to your organization's data. Your organization exceeded the limit of the current plan.">
                <InfoCircleOutlined style={{ margin: 0, cursor: 'pointer' }} />
              </Tooltip>
            </ReadOnlyBanner>
          )}
          {headerContent && (
            <header
              style={{
                ...headerDefaultStyle,
                borderBottom: `1px solid ${themeConfig[theme].colors.border}`,
                backgroundColor: themeConfig[theme].colors.layoutBg,
                ...headerStyle,
              }}
            >
              {headerContent}
              {isAuthenticated && (
                <Row
                  justifyContent="flex-end"
                  gap={25}
                  style={{ width: 'fit-content' }}
                >
                  <PageLayoutNotifications />
                  <PageLayoutSettingsMenuBtn />
                </Row>
              )}
            </header>
          )}
          <Layout>
            {sidebarLeftContent && (
              <SiderWrapper
                $backgroundColor={themeConfig[theme].colors.layoutBg}
                style={{
                  backgroundColor: themeConfig[theme].colors.layoutBg,
                  borderRight: `1px solid ${themeConfig[theme].colors.border}`,
                }}
              >
                <Sider
                  data-testid="sidebar-left"
                  ref={sidebarLeftRef}
                  collapsible={sidebarLeftCollapsed !== undefined}
                  collapsed={!sidebarLeftVisible}
                  collapsedWidth={0}
                  trigger={null}
                  width={sidebarLeftWidth}
                  style={{
                    backgroundColor: themeConfig[theme].colors.layoutBg,
                  }}
                >
                  {sidebarLeftContent}
                </Sider>
              </SiderWrapper>
            )}
            <Layout
              style={{
                position: 'relative',
                background: themeConfig[theme].colors.bgBase,
              }}
            >
              <ContentWrapper
                style={{
                  paddingTop: isMobileView ? '45px' : '30px',
                  background: themeConfig[theme].colors.layoutBg,
                  ...contentStyle,
                }}
              >
                {children}
              </ContentWrapper>
              {showCloseSidebarButtons && (
                <FooterActions>
                  <Tooltip
                    title={
                      sidebarLeftVisible
                        ? leftSidebarOpenedTooltip
                        : leftSidebarClosedTooltip
                    }
                    mouseEnterDelay={0.5}
                    placement="right"
                  >
                    <Button
                      data-type="sidebar-button-left"
                      shape="circle"
                      icon={
                        <div id="sidebar-button-left-icon">
                          <LeftOutlined
                            style={{
                              transform: `${
                                sidebarLeftVisible ? 'none' : 'rotate(180deg)'
                              }`,
                              transition: '.25s all',
                              fontSize: '12px',
                              pointerEvents: 'none',
                            }}
                          />
                        </div>
                      }
                      onClick={() =>
                        setSidebarLeftVisible((currentValue) => !currentValue)
                      }
                      style={footerButtonStyle}
                    />
                  </Tooltip>
                  <Tooltip
                    title={
                      sidebarRightVisible
                        ? rightSidebarOpenedTooltip
                        : rightSidebarClosedTooltip
                    }
                    mouseEnterDelay={0.5}
                    placement="left"
                  >
                    <Button
                      data-type="sidebar-button-right"
                      shape="circle"
                      icon={
                        <div id="sidebar-button-right-icon">
                          <LeftOutlined
                            style={{
                              transform: `${
                                sidebarRightVisible ? 'rotate(180deg)' : 'none'
                              }`,
                              transition: '.25s all',
                              fontSize: '12px',
                              pointerEvents: 'none',
                            }}
                          />
                        </div>
                      }
                      onClick={() =>
                        setSidebarRightVisible((currentValue) => !currentValue)
                      }
                      style={footerButtonStyle}
                    />
                  </Tooltip>
                </FooterActions>
              )}
              {footerContent && <Footer>{footerContent}</Footer>}
            </Layout>
            {sidebarRightContent && (
              <SiderWrapper
                $backgroundColor={themeConfig[theme].colors.layoutBg}
                style={{
                  backgroundColor: themeConfig[theme].colors.layoutBg,
                  borderLeft: `1px solid ${themeConfig[theme].colors.border}`,
                }}
              >
                <Sider
                  ref={sidebarRightRef}
                  collapsible={sidebarRightCollapsed !== undefined}
                  collapsed={!sidebarRightVisible}
                  collapsedWidth={0}
                  width={sidebarRightWidth}
                  trigger={null}
                  style={{
                    height: '100%',
                    backgroundColor: themeConfig[theme].colors.layoutBg,
                  }}
                >
                  {sidebarRightContent}
                </Sider>
              </SiderWrapper>
            )}
          </Layout>
        </Layout>
      </Space>
    );
  },
);

const containerStyle: React.CSSProperties = {
  height: '100vh',
  overflow: 'hidden',
};

const headerDefaultStyle: React.CSSProperties = {
  textAlign: 'center',
  height: 50,
  minHeight: 50,
  paddingInline: 16,
  lineHeight: '64px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const ContentWrapper = styled(Content)`
  padding: 25px;
  padding-bottom: 40px;
  overflow-y: auto;
`;

const SiderWrapper = styled.div<{ $backgroundColor: string }>`
  .ant-menu {
    background: ${({ $backgroundColor }) => $backgroundColor} !important;
  }
  .ant-menu-light.ant-menu-root.ant-menu-inline {
    border-inline-end: 0px !important;
  }
`;

const Sider = styled(AntdSider)`
  maxheight: calc(100vh - ${headerDefaultStyle.height});
  overflow-y: 'auto';
`;

const FooterActions = styled(Row)`
  position: absolute;
  padding-inline: 10px;
  padding-right: 26px;
  bottom: 10px;
  left: 0;
  z-index: 4;
  border-radius: 8px;
  width: 100%;
  justify-content: space-between;
`;

const footerButtonStyle: React.CSSProperties = {
  boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
};

const ReadOnlyBanner = styled(Row)`
  min-height: 40px;
  border-bottom: 1px solid #fff;
  background-color: #c9184a;
  color: #fff;
`;
